import React, { useState, useEffect, memo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@mui/styles';

import Popper from '@mui/material/Popper';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import ToolIcon from '@mui/icons-material/Settings';
import CachedIcon from '@mui/icons-material/Cached';

import Tooltip from './styled/tooltip';
import StyledPaper from './styled/paper';

import CountryFlag from './country-flag';
import countries from '../data/country-codes';
import Auth from '../data/auth-service';
import DataContext from '../data/data-context.js';
import AssetsService from '../data/assets-service.js';
import { CmcContext } from '../data/cmcs-service';
import eventBus from '../libraries/eventbus.js';
import Helpers from '../libraries/helpers';

const useStyles = makeStyles(theme => ({
    root: {
        overflow: 'hidden',
        whiteSpace: 'inherit', // 'normal' in the detail view and 'nowrap' in the table
        wordBreak: 'break-all',
        textOverflow: 'ellipsis',
    },
    lookup: {
        justifyContent: 'left',
        textAlign: 'left',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        padding: theme.spacing(1),
    },
    title: {
        padding: '8px 24px 8px 12px',
    },
    break: {
        opacity: 0.5,
        paddingLeft: 10,
    },
    popper: {
        zIndex: theme.zIndex.modal,
    },
    gear: {
        pointerEvents: 'all',
        paddingTop: 0,
    },
}));

function IPAddress (props) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [iplookups, setIplookups] = useState([]);
    const [name, setName] = useState('');
    const [editName, setEditName] = useState('');
    const [data, setData] = useState({});
    const { assets, lookups, updateAsset, createAsset, showIP, updateShowIP } = useContext(DataContext);
    const { cmc } = useContext(CmcContext);
    const [readonly] = useState(!Auth.permissions.get('Assets').write);
    const [UUID] = useState(Helpers.UniqueID());

    const handleOnIpTool = useCallback(e => {
        // toggle the popper
        setAnchorEl(current => current === e.currentTarget ? null : e.currentTarget);
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleClose = (e) => {
        // close all the poppers
        eventBus.dispatch('closeIPTool', {});

        setAnchorEl(null);
        e.preventDefault();
        e.stopPropagation();
    };

    const stopPropagation = e => {
        e.stopPropagation();
    };

    const handleAssetRevert = () => {
        const a = assets[props.address];
        setEditName(a ? a.name : '');
    };

    const handleAssetChange = e => {
        setEditName(e.target.value);
    };

    const handleShowIPChange = e => {
        updateShowIP(e.target.checked);
    };

    const handleOnSave = () => {
        if (assets[props.address]) updateAsset({ ip: props.address, name: editName, data });
        else createAsset({ ip: props.address, name: editName });
        setName(editName);
        setAnchorEl(null);
    };

    const handleCmcDnsLookup = () => {
        AssetsService.passiveLookup(props.address, (cmc || {}).name).then(res => {
            if (res && res.length > 0) {
                setEditName(res[0]);
            }
        });
    };

    const handleCmcLookup = () => {
        AssetsService.getLookup(props.address, (cmc || {}).name).then(res => {
            // CMC return names with '.' at the end
            if (res && res.length > 0) {
                setEditName(res[0].slice(-1) === '.' ? res[0].slice(0, -1) : res[0]);
            }
        });
    };

    const handleSensorLookup = () => {
        AssetsService.getLookupFromSensor(props.uuid, props.address, (cmc || {}).name).then(res => {
            // protect from same '.' syndrome for a symmetry
            if (res && res.length > 0) {
                setEditName(res[0].slice(-1) === '.' ? res[0].slice(0, -1) : res[0]);
            }
        });
    };

    useEffect(() => {
        setIplookups(lookups.map(d => ({ ...d, link: d.url.replace('$addr', props.address) })));
    }, [props.address, lookups]);

    useEffect(() => {
        const a = assets[props.address];
        setName(a ? a.name : '');
        setEditName(a ? a.name : '');
        setData(a ? a.data : {});
    }, [assets, props.address]);

    useEffect(() => {
        if (anchorEl) {
            eventBus.dispatch('closeIPTool', { except: UUID });

            // add a one time event listener for closing this popper
            eventBus.on('closeIPTool', function handler ({ except }) {
                setAnchorEl(null);
            }, true);
        }
    }, [anchorEl, UUID]);

    return (
        <div className={classes.root} style={props.style || {}}>
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" wrap="nowrap">
                {props.country &&
                    <Grid item>
                        <Tooltip title={countries[props.country][0] || ''} arrow placement="top">
                            <CountryFlag country={props.country}/>
                        </Tooltip>
                    </Grid>
                }
                <Grid item>
                    {(props.address || name.length > 0) && <IconButton
                        className={classes.gear}
                        color="primary"
                        aria-label="ip-tool"
                        size="small"
                        onClick={handleOnIpTool}>
                        <ToolIcon fontSize="inherit"/>
                    </IconButton>}
                </Grid>

                {name.length > 0 &&
                    <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" wrap="nowrap">
                        <Tooltip
                            title={`${props.tipPrefix || ''}${props.address}`}
                            arrow
                            placement="top">
                            <Grid item>
                                {name}
                            </Grid>
                        </Tooltip>
                        {showIP &&
                            <Grid item className={classes.break}>
                                {props.address}
                            </Grid>
                        }
                    </Grid>
                }

                {name.length < 1 &&
                    <Tooltip
                        title={`${props.tipPrefix || ''}${props.address}`}
                        arrow
                        placement="top">
                        <Grid item>
                            {props.address}
                        </Grid>
                    </Tooltip>
                }
            </Grid>

            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                className={classes.popper}>
                <ClickAwayListener onClickAway={handleClose}>
                    <StyledPaper className={classes.paper} onClick={stopPropagation}>
                        <Grid container direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" className={classes.title}>
                                {props.address}
                            </Typography>
                            <FormControlLabel
                                size="small"
                                control={<Checkbox size="small" checked={showIP} onChange={handleShowIPChange}/>}
                                label="Show IP Address"
                                aria-label="show IP Address"/>
                        </Grid>
                        <DialogContent dividers>
                            <Typography variant="subtitle1"> Research </Typography>
                            <div>
                                {iplookups.map((m, i) => (
                                    <div key={`${m.name}_${i}`} style={{ paddingTop: 5 }}>
                                        <Link
                                            className={classes.lookup}
                                            aria-label={m.name}
                                            href={m.link}
                                            target="_blank"
                                            rel="noreferrer">
                                            {m.name}
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </DialogContent>
                        <DialogContent dividers>
                            <div>
                                <FormControl variant="outlined" style={{ width: '100%' }}>
                                    <InputLabel htmlFor="outlined-adornment-asset">Asset Name</InputLabel>
                                    <OutlinedInput
                                        disabled={readonly}
                                        id="outlined-adornment-asset"
                                        type='text'
                                        size="small"
                                        value={editName}
                                        onChange={handleAssetChange}
                                        endAdornment={
                                            !readonly && <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    aria-label="revert"
                                                    onClick={handleAssetRevert}
                                                    edge="end">
                                                    <CachedIcon fontSize="small"/>
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>
                            </div>
                        </DialogContent>
                        {!readonly && <DialogActions>
                            <Button
                                title="Passive IP Lookup using CMC DNS data"
                                aria-label="CMC DNS Lookup IP"
                                onClick={handleCmcDnsLookup}>
                                dns P
                            </Button>
                            {props.uuid && <Button
                                title="Reverse IP Lookup from originating Sensor"
                                aria-label="Sensor Lookup IP"
                                onClick={handleSensorLookup}>
                                dns S
                            </Button>}
                            <Button
                                title="Reverse IP Lookup from CMC"
                                aria-label="CMC Lookup"
                                onClick={handleCmcLookup}>
                                dns C
                            </Button>
                            <Button
                                autoFocus
                                aria-label="save"
                                disabled={!editName.length}
                                onClick={handleOnSave}
                                color="primary">
                                Save
                            </Button>
                        </DialogActions>}
                    </StyledPaper>
                </ClickAwayListener>
            </Popper>
        </div>
    );
}

IPAddress.propTypes = {
    address: PropTypes.string,
    country: PropTypes.string,
    // this is the sensor uuid
    uuid: PropTypes.string,
    tipPrefix: PropTypes.string,
    style: PropTypes.object,
};

export default memo(IPAddress);
